/* eslint-disable @typescript-eslint/ban-ts-comment */
import { t } from 'i18next';
import { debounce } from 'lodash';
import type { SliderProps } from 'rc-slider';
import RcSlider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { useState } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import { Input } from '../input';
import Typography from '../typography/typography';

export interface IEmployeeSlider extends SliderProps {
  label?: string;
  isDisabled: boolean;
  max: number;
  min: number;
  startPoint?: number;
  step: number;
  onChange?: (value: number, fromInput?: boolean) => void;
  showInvalidMessage?: boolean;
}

export function EmployeeSlider({
  label = '',
  max,
  min,
  startPoint,
  step,
  isDisabled,
  onChange,
  showInvalidMessage = false,
  ...rest
}: IEmployeeSlider) {
  const theme = useTheme();
  const { value } = rest;
  const [inputValue, setInputValue] = useState(value);

  return (
    <>
      <SliderContainer>
        <StyledTypography
          variant={TypographyVariant.bodyMd2}
          $extraMarginRight={!value}
        >
          {label ? `${label}` : '% You Contribute'}
        </StyledTypography>
        {typeof value === 'number' && (
          <StyledInput
            key={`${inputValue}${value}`}
            name={'value'}
            showLabel={false}
            type="number"
            min={min}
            max={max}
            defaultValue={value.toString()}
            width="140px"
            height="38px"
            onChange={debounce((event: React.ChangeEvent<HTMLInputElement>) => {
              const numValue = Number(event.target.value);
              onChange?.(numValue, true);
              setInputValue(numValue);
            }, 1000)}
            disabled={isDisabled}
          />
        )}
        <RcSlider
          max={max}
          startPoint={startPoint}
          step={step}
          className="employee-slider"
          railStyle={{
            backgroundColor: 'transparent',
          }}
          trackStyle={{
            background: `${theme.palette.primary.main}`,
            zIndex: 3,
            height: '30px',
            borderRadius: '100px 0px 0px 100px',
          }}
          handleStyle={{
            height: '30px',
            width: '30px',
            background: `${theme.palette.neutral[50]}`,
            border: `1px solid ${theme.palette.primary.main}`,
            marginTop: '0px',
            marginLeft: '0px',
            zIndex: 4,
            boxShadow: `0px 4px 10px ${theme.palette.white(0.2)}`,
          }}
          disabled={isDisabled}
          onChange={onChange}
          ariaLabelForHandle="slider handle for employee slider"
          data-testid={`${
            label ? label.replaceAll(' ', '_') : ''
          }-employee-slider`}
          {...rest}
        />
      </SliderContainer>
      <TextContainer>
        {showInvalidMessage && (
          <ErrorTypography
            variant={TypographyVariant.bodySm2}
            color={theme.palette.neutral[90]}
          >
            {t('errors.invalidValueError')}
          </ErrorTypography>
        )}
      </TextContainer>
    </>
  );
}

export default EmployeeSlider;

const StyledTypography = styled(Typography)`
  ${({
    theme,
    $extraMarginRight,
  }: {
    theme: ITheme;
    $extraMarginRight: boolean;
  }) => css`
  text-align: right;
  margin-right: ${$extraMarginRight ? theme.spacing(2) : theme.spacing(0.5)}};
  width: 100%;
  max-width: 90px;
  `}
`;

const TextContainer = styled.div`
  ${({ theme }) => css`
    min-height: 16px;
    margin: ${theme.spacing(1.5, 0)};
  `}
`;

const ErrorTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(1.5)};
    margin-left: ${theme.spacing(2)};
  `}
`;

const StyledInput = styled(Input)`
  ${({ theme }) => css`
    ${theme.typography.bodyMd1};
  `}
`;

const SliderContainer: StyledComponent<
  'div',
  DefaultTheme,
  Record<string, unknown> & { link?: string },
  never
> = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 30px;
    display: flex;
    gap: ${theme.spacing(3)};
    justify-content: center;
    align-items: center;
    flex-direction: row;

    .rc-slider {
      height: 30px;
      padding: 0px;
      margin-right: ${theme.spacing(1)};
    }

    .rc-slider-step {
      z-index: 1;
      background: ${({ theme }) => theme.palette.neutral[50]};
      height: 30px;
      border-radius: 100px;
    }

    .rc-slider-disabled {
      background: none;
    }

    .rc-slider-handle {
      &:focus {
        outline: 2px solid ${theme.palette.focus};
        outline-offset: -1px;
      }
    }
  `}
`;
